<template>
  <app-layout>
    <div class="sc-container">
      <div class="page-wrap">

        <!-- Sidebar -->
        <aside class="page-sidebar">
          <div class="sidebar-card">
            <div class="sidebar-profile">
              <img
                v-if="user.avatar"
                class="sidebar-avatar"
                :src="$avatarSrc(user.avatar, 'users')"
                alt="User avatar"
              />
              <img
                v-else
                class="sidebar-avatar"
                src="/assets/avatar.png"
                alt="User avatar"
              />
              <div class="sidebar-name">{{ user.fname }} {{ user.lname }}</div>
              <div class="sidebar-sub" v-if="user.currentcompany">
                {{ user.current_position }} &mdash; {{ user.currentcompany.name }}
              </div>
              <div class="sidebar-sub" v-else>{{ user.profession }}</div>
            </div>
            <nav class="sidebar-nav">
              <router-link class="sidebar-link" to="/profile/update">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                Edit Profile
              </router-link>
              <router-link class="sidebar-link" to="/profile/change-password">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Change Password
              </router-link>
              <router-link class="sidebar-link active" to="/notification-settings">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                Notification Settings
              </router-link>
              <router-link class="sidebar-link" to="/my-Projects">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 20h20M4 20V10l8-6 8 6v10"/></svg>
                My Projects
              </router-link>
              <router-link class="sidebar-link" to="/my-followers">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                Following
              </router-link>
              <router-link class="sidebar-link" to="/my-blog-history">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                My Blog
              </router-link>
              <router-link class="sidebar-link" to="/my-service-requests">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                Service Requests
              </router-link>
            </nav>
          </div>
        </aside>

        <!-- Main content -->
        <main class="page-main">
          <div class="main-card">
            <div class="main-card-header">
              <h2 class="main-card-title">Notification Settings</h2>
              <p class="main-card-subtitle">Choose which email notifications you want to receive.</p>
            </div>

            <div class="main-card-body">
              <div v-if="successMsg" class="alert alert-success">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                {{ successMsg }}
              </div>

              <!-- Notification toggles -->
              <div class="notif-section">
                <div class="notif-section-title">Email Notifications</div>

                <div class="toggle-row">
                  <div class="toggle-info">
                    <div class="toggle-label">Listing notifications</div>
                    <div class="toggle-desc">Get emails about new comments, likes, and activity on your listings.</div>
                  </div>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      :checked="notification.listing_email == 1"
                      @change="notification.listing_email = $event.target.checked ? 1 : 0"
                    />
                    <span class="toggle-track"></span>
                  </label>
                </div>

                <div class="toggle-row">
                  <div class="toggle-info">
                    <div class="toggle-label">Service notifications</div>
                    <div class="toggle-desc">Get emails about new service requests matching your profession and applications.</div>
                  </div>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      :checked="notification.service_email == 1"
                      @change="notification.service_email = $event.target.checked ? 1 : 0"
                    />
                    <span class="toggle-track"></span>
                  </label>
                </div>

                <div class="toggle-row">
                  <div class="toggle-info">
                    <div class="toggle-label">Messenger notifications</div>
                    <div class="toggle-desc">Get emails when you receive new messages from other users.</div>
                  </div>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      :checked="notification.messenger_email == 1"
                      @change="notification.messenger_email = $event.target.checked ? 1 : 0"
                    />
                    <span class="toggle-track"></span>
                  </label>
                </div>
              </div>

              <div class="form-actions">
                <router-link to="/" class="btn-outline">Cancel</router-link>
                <button class="btn-primary" @click="updateNotification" :disabled="loading">
                  <span v-if="loading" class="spinner"></span>
                  <span>{{ loading ? 'Saving...' : 'Save Settings' }}</span>
                </button>
              </div>
            </div>
          </div>
        </main>

      </div>
    </div>
  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout.vue';
import axios from 'axios';

export default {
  name: 'NotificationSetting',
  components: { AppLayout },

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      company: {},
      user: {},
      loading: false,
      id: localStorage.getItem('slug'),
      successMsg: '',
      notification: {
        listing_email: 0,
        service_email: 0,
        messenger_email: 0,
      },
    };
  },

  created() {
    this.fetchCompany();
    this.fetchUser();
  },

  methods: {
    logout() {
      localStorage.clear();
      this.$router.push({ name: 'Login' });
    },

    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company')
        .then(res => { if (res.status === 200) this.company = res.data; })
        .catch(() => {});
    },

    fetchUser() {
      axios(process.env.VUE_APP_API_URL + 'user/details/' + localStorage.getItem('user'))
        .then(res => {
          if (res.status === 200) {
            this.user = res.data;
            if (res.data.notification_setting) {
              this.notification.listing_email = res.data.notification_setting.listing_email;
              this.notification.service_email = res.data.notification_setting.service_email;
              this.notification.messenger_email = res.data.notification_setting.messenger_email;
            }
          }
        })
        .catch(() => {});
    },

    updateNotification() {
      this.loading = true;
      this.successMsg = '';
      const fd = new FormData();
      fd.append('listing_email', this.notification.listing_email);
      fd.append('service_email', this.notification.service_email);
      fd.append('messenger_email', this.notification.messenger_email);
      axios.post(process.env.VUE_APP_API_URL + 'user/update-notification-setting/' + localStorage.getItem('user'), fd)
        .then(res => {
          if (res.status === 200) { this.successMsg = 'Settings saved.'; }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
.page-wrap {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  padding: 2.5rem 0;
}

/* ── Sidebar ── */
.page-sidebar {
  position: sticky;
  top: 80px;
  align-self: start;
}

.sidebar-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.sidebar-profile {
  padding: 1.5rem 1rem;
  text-align: center;
  border-bottom: 1px solid var(--border);
}

.sidebar-avatar {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  object-fit: cover;
  border: 3px solid var(--border);
}

.sidebar-name {
  margin-top: 0.75rem;
  font-weight: 700;
  color: var(--text);
  font-size: 0.95rem;
}

.sidebar-sub {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.sidebar-nav {
  padding: 0.5rem 0;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.875rem;
  color: var(--text-muted);
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}

.sidebar-link:hover,
.sidebar-link.active {
  background: var(--surface-2);
  color: var(--primary);
}

.sidebar-link.active {
  font-weight: 600;
  border-right: 3px solid var(--accent);
}

.sidebar-link svg {
  flex-shrink: 0;
}

/* ── Main card ── */
.page-main {
  min-width: 0;
}

.main-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.main-card-header {
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid var(--border);
}

.main-card-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 0.25rem;
}

.main-card-subtitle {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0;
}

.main-card-body {
  padding: 1.75rem;
}

/* ── Alert ── */
.alert {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.875rem 1rem;
  border-radius: var(--radius);
  font-size: 0.875rem;
  margin-bottom: 1.25rem;
}

.alert-success {
  background: #ecfdf5;
  color: var(--success);
  border: 1px solid #a7f3d0;
}

/* ── Notification section ── */
.notif-section {
  margin-bottom: 1.5rem;
}

.notif-section-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 0;
  border-bottom: 1px solid var(--border-light);
}

.toggle-row:last-child {
  border-bottom: none;
}

.toggle-info {
  flex: 1;
  min-width: 0;
}

.toggle-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 0.2rem;
}

.toggle-desc {
  font-size: 0.8rem;
  color: var(--text-muted);
  line-height: 1.4;
}

/* Custom toggle switch */
.toggle-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-track {
  display: block;
  width: 44px;
  height: 24px;
  background: var(--border);
  border-radius: var(--radius-full);
  position: relative;
  transition: background 0.2s;
}

.toggle-track::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background: #fff;
  border-radius: 50%;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s;
}

.toggle-switch input:checked + .toggle-track {
  background: var(--success);
}

.toggle-switch input:checked + .toggle-track::after {
  transform: translateX(20px);
}

/* ── Form actions ── */
.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border-light);
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-right: 0.4rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .page-wrap {
    grid-template-columns: 1fr;
    padding: 1.5rem 0;
  }

  .page-sidebar {
    position: static;
  }

  .sidebar-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.5rem;
  }

  .sidebar-link {
    padding: 0.5rem 0.75rem;
    border-radius: var(--radius);
    font-size: 0.8rem;
  }

  .sidebar-link.active {
    border-right: none;
    background: var(--surface-3);
  }
}
</style>
